/**
 * Orders Tab Component - the supplier's full purchase order history.
 *
 * Formerly showed only the ten most recent orders and a "View All" button that
 * bounced to Purchasing. That hid the true order count (ten-of-ten and
 * ten-of-four-hundred looked identical) and made the tab useless for any
 * supplier with real volume. It now pages server-side through everything.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Package, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { PurchaseOrder, SupplierProfile } from '../../lib/api';
import { getStatusBadgeColor } from './utils';
import { useFormatters } from '../../contexts/PreferencesContext';

interface OrdersTabProps {
  supplier: SupplierProfile;
  orders: PurchaseOrder[];
  ordersLoading: boolean;
  formatCurrency: (value: string | number) => string;
  /** 1-based current page. */
  ordersPage?: number;
  /** Unpaginated total from the API. */
  ordersCount?: number;
  /** Rows per page. */
  ordersPageSize?: number;
  /** CR-7028: Current supplier code filter value. */
  ordersSupplierCode?: string;
  /** Load a given page. */
  fetchOrders?: (page?: number, supplierCode?: string) => void | Promise<void>;
  /** CR-7028: Set the supplier code filter. */
  setOrdersSupplierCode?: (code: string) => void;
}

export function OrdersTab({
  supplier,
  orders,
  ordersLoading,
  formatCurrency,
  ordersPage = 1,
  ordersCount = 0,
  ordersPageSize = 25,
  ordersSupplierCode = '',
  fetchOrders,
  setOrdersSupplierCode,
}: OrdersTabProps) {
  const { formatDate } = useFormatters();
  const navigate = useNavigate();

  // CR-7028: Local state for filter input (before applying)
  const [supplierCodeInput, setSupplierCodeInput] = useState(ordersSupplierCode);

  const total = ordersCount || orders.length;
  const totalPages = Math.max(1, Math.ceil(total / ordersPageSize));
  const firstRow = total === 0 ? 0 : (ordersPage - 1) * ordersPageSize + 1;
  const lastRow = Math.min(ordersPage * ordersPageSize, total);
  const canPage = typeof fetchOrders === 'function';
  const canFilter = typeof fetchOrders === 'function' && typeof setOrdersSupplierCode === 'function';

  // CR-7028: Apply supplier code filter
  const handleApplyFilter = () => {
    if (!canFilter) return;
    setOrdersSupplierCode(supplierCodeInput);
    fetchOrders(1, supplierCodeInput); // Reset to page 1 when filtering
  };

  // CR-7028: Clear supplier code filter
  const handleClearFilter = () => {
    if (!canFilter) return;
    setSupplierCodeInput('');
    setOrdersSupplierCode('');
    fetchOrders(1, ''); // Reset to page 1 with no filter
  };

  // CR-7028: Handle Enter key in filter input
  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleApplyFilter();
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Purchase Orders
            {total > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({total.toLocaleString()})
              </span>
            )}
          </CardTitle>
          {/* CR-7028: Supplier code filter */}
          {canFilter && (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Filter by supplier code..."
                  value={supplierCodeInput}
                  onChange={(e) => setSupplierCodeInput(e.target.value)}
                  onKeyDown={handleFilterKeyDown}
                  className="w-48 pr-8"
                  disabled={ordersLoading}
                />
                {supplierCodeInput && (
                  <button
                    type="button"
                    onClick={handleClearFilter}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear filter"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleApplyFilter}
                disabled={ordersLoading}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {ordersLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No purchase orders found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/purchasing?tab=po_lines&poFilter=${encodeURIComponent(order.po_number)}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold">{order.po_number}</p>
                    <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                  </div>
                  <Badge className={`${getStatusBadgeColor(order.status)} capitalize px-3 py-1 text-xs font-medium`}>
                    {order.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Issue Date: </span>
                    <span>{formatDate(order.issue_date)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Value: </span>
                    <span className="font-semibold">
                      {order.currency} {formatCurrency(order.total_value)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Lines: </span>
                    <span>{order.line_count || 0}</span>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t">
              <span className="text-sm text-muted-foreground">
                Showing {firstRow.toLocaleString()}–{lastRow.toLocaleString()} of{' '}
                {total.toLocaleString()} order{total === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-2">
                {canPage && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={ordersPage <= 1 || ordersLoading}
                      onClick={() => fetchOrders(ordersPage - 1)}
                      aria-label="Previous page of orders"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-1">
                      Page {ordersPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={ordersPage >= totalPages || ordersLoading}
                      onClick={() => fetchOrders(ordersPage + 1)}
                      aria-label="Next page of orders"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/purchasing?tab=po_lines&supplier=${supplier.company}`)}
                >
                  Open in Purchasing
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
